import { Controller } from "@hotwired/stimulus"
import Cropper from "cropperjs"

export default class extends Controller {
  static targets = ["input", "preview", "modal", "cropperImage", "saveButton", "cancelButton"]
  static values = {
    aspectRatio: Number, // Optional, e.g., 1 for square (avatar), 16/9 for banner
    maxSize: Number // Optional max size in MB
  }

  connect() {
    this.cropper = null
  }

  disconnect() {
    this.destroyCropper()
  }

  // Called when the user selects a file
  loadFile(event) {
    const file = event.target.files[0]
    if (!file) return

    // Optional size validation
    if (this.hasMaxSizeValue && file.size > this.maxSizeValue * 1024 * 1024) {
      alert(`ファイルサイズは ${this.maxSizeValue}MB 以下にしてください。`)
      event.target.value = "" // Reset
      return
    }

    // Read the file
    const reader = new FileReader()
    reader.onload = (e) => {
      // Set the source and wait for it to load before starting the cropper
      this.cropperImageTarget.onload = () => {
        this.showModal()
        this.initCropper()
        // Clear onload to avoid loops
        this.cropperImageTarget.onload = null
      }
      this.cropperImageTarget.src = e.target.result
    }
    reader.readAsDataURL(file)
  }

  // Initialize Cropper.js
  initCropper() {
    this.destroyCropper()

    const options = {
      viewMode: 0, // Changed from 1 to 0 to allow the crop box to go outside the canvas (more flexibility)
      dragMode: 'move',
      autoCropArea: 0.8,
      restore: false,
      guides: true,
      center: true,
      highlight: false,
      cropBoxMovable: true,
      cropBoxResizable: true,
      toggleDragModeOnDblclick: false,
      responsive: true,
      checkOrientation: true,
      wheelZoomRatio: 0.1,
    }

    if (this.hasAspectRatioValue) {
      options.aspectRatio = this.aspectRatioValue
    }

    this.cropper = new Cropper(this.cropperImageTarget, options)
  }

  destroyCropper() {
    if (this.cropper) {
      this.cropper.destroy()
      this.cropper = null
    }
  }

  showModal() {
    this.modalTarget.classList.remove("hidden")
    document.body.style.overflow = "hidden"
  }

  hideModal() {
    this.modalTarget.classList.add("hidden")
    document.body.style.overflow = ""
    this.destroyCropper()
  }

  cancel(event) {
    if (event) event.preventDefault()
    this.hideModal()
    this.inputTarget.value = "" 
  }

  save(event) {
    if (event) event.preventDefault()
    
    if (!this.cropper) return

    // Get cropped canvas
    const canvas = this.cropper.getCroppedCanvas({
       maxWidth: 2000, 
       maxHeight: 2000,
       imageSmoothingEnabled: true,
       imageSmoothingQuality: 'high',
    })

    // Determine MIME type and quality
    const originalFile = this.inputTarget.files[0]
    const isPng = originalFile && originalFile.type === 'image/png'
    const mimeType = isPng ? 'image/png' : 'image/jpeg'
    const quality = isPng ? 1.0 : 0.85

    // Convert canvas to Blob
    canvas.toBlob((blob) => {
      if (!blob) return

      // Create a unique filename
      const originalName = originalFile?.name || 'image.jpg'
      const parts = originalName.split('.')
      const ext = parts.pop().toLowerCase()
      const baseName = parts.join('.')
      const newName = `${baseName}_cropped_${Date.now()}.${isPng ? 'png' : 'jpg'}`

      // Create a new File object
      const file = new File([blob], newName, { type: mimeType, lastModified: new Date().getTime() })

      // Replace the file input's files
      const dataTransfer = new DataTransfer()
      dataTransfer.items.add(file)
      this.inputTarget.files = dataTransfer.files

      // Update the preview image
      const previewUrl = URL.createObjectURL(blob)
      if (this.hasPreviewTarget) {
        if (this.previewTarget.tagName.toLowerCase() === 'img') {
          this.previewTarget.src = previewUrl
        } else {
          // Remove potential background gradients
          this.previewTarget.className = this.previewTarget.className.replace(/bg-gradient-to-[^ ]+/g, '')
          // Set new background
          this.previewTarget.style.backgroundImage = `url(${previewUrl})`
          this.previewTarget.style.backgroundColor = 'transparent'
          this.previewTarget.innerHTML = "" 
        }
      }

      this.hideModal()
    }, mimeType, quality)
  }
}
